import type { ApiClient } from "@/shared/cache/types";
import type { Entry } from "@/shared/types/entries";

type EntryGroupStatus = "tracked" | "reviewed" | "exported" | "deleted";

type EntryGroupList = {
	object: "list";
	url: "/v2/entry-groups";
	hasMore: boolean;
	data: EntryGroup[];
};

type EntryGroup = {
	id: string;
	status: EntryGroupStatus;
	matterId: string | null;
	lastEntryAt?: string | null;
	createdAt?: string | null;
	taskCodeId?: string | null;
	activityCodeId?: string | null;
	taskCode?: { id?: string | null; code?: string | null } | null;
	activityCode?: { id?: string | null; code?: string | null } | null;
	durationSeconds: number;
	narrative: string | null;
	entries?: Array<{
		id: string;
		startTime: string;
		endTime: string;
		applicationId: number | null;
		matterId: string | null;
		summary: string | null;
		durationSeconds: number;
	}>;
};

function parseNumericId(id: string): number {
	const [, numeric] = id.split("_");
	const parsed = Number(numeric ?? id);
	return Number.isFinite(parsed) ? parsed : 0;
}

function toLegacyEntry(group: EntryGroup): Entry {
	const firstEntry = group.entries?.[0];
	const lastEntry = group.entries?.[group.entries.length - 1];
	const start = firstEntry?.startTime ? new Date(firstEntry.startTime) : null;
	const end = firstEntry?.endTime ? new Date(firstEntry.endTime) : null;
	const lastStart = lastEntry?.startTime ? new Date(lastEntry.startTime) : null;
	const fallbackDate = group.lastEntryAt ? new Date(group.lastEntryAt) : null;
	const dateSource = lastStart ?? fallbackDate;

	return {
		id: parseNumericId(group.id),
		date:
			dateSource && !Number.isNaN(dateSource.getTime())
				? dateSource.toLocaleDateString("en-US", { timeZone: "UTC" })
				: "",
		matter: group.matterId ?? firstEntry?.matterId ?? "",
		matter_id: group.matterId ?? firstEntry?.matterId ?? "",
		matter_name: group.matterId ?? firstEntry?.matterId ?? "",
		client: "",
		description: group.narrative ?? firstEntry?.summary ?? "",
		task: group.taskCode?.code ?? group.taskCodeId ?? "",
		activity: group.activityCode?.code ?? group.activityCodeId ?? "",
		document_name: "",
		duration: group.durationSeconds / 3600,
		units_seconds: group.durationSeconds,
		start_time:
			start && !Number.isNaN(start.getTime()) ? start.toISOString() : "",
		end_time: end && !Number.isNaN(end.getTime()) ? end.toISOString() : "",
		application: "",
		billable: null,
		approved: group.status === "reviewed" || group.status === "exported",
		needs_improvement: false,
		attorney_feedback: null,
	};
}

export async function fetchLegacyEntriesFromEntryGroups(
	api: ApiClient,
): Promise<{
	entries: Entry[];
	exportedEntries: Entry[];
	deletedEntries: Entry[];
}> {
	const queryParams: Record<string, string | number> = { limit: 1000 };

	const response = await api.makeApiRequest({
		endpoint: "/v2/entry-groups",
		method: "GET",
		queryParams,
	});
	const body = response.data as EntryGroupList | undefined;
	const groups = body?.data ?? [];
	const entries = groups.map(toLegacyEntry);

	return {
		entries: entries.filter(
			(_, index) =>
				groups[index]?.status === "tracked" ||
				groups[index]?.status === "reviewed",
		),
		exportedEntries: entries.filter(
			(_, index) => groups[index]?.status === "exported",
		),
		deletedEntries: entries.filter(
			(_, index) => groups[index]?.status === "deleted",
		),
	};
}
